import { useQuery } from '@tanstack/react-query';
import { trim } from 'fp-ts/lib/string';
import { useMemo } from 'react';
import { AirportPartners, City, CityMap } from 'shared';

import { fetchLocations } from '../domain/api';
import { generateKey } from '../domain/utils';

export function useLocations() {
  const payload = { partner: AirportPartners.SKYTEAM };

  const key = generateKey(payload);

  const query = useQuery({
    queryKey: ['sky-locations', key],
    queryFn: () => fetchLocations(payload),
  });

  const { data, isSuccess, isError, error: queryError } = query;

  const searchData = useMemo(() => {
    return data ? data : null;
  }, [data]);

  const searchLocations = (searchTerm: string) => {
    return search(searchTerm, searchData);
  };

  const getLocationByIdentifier = (identifier: string): City | undefined => {
    return getByIdentifier(identifier, searchData) || undefined;
  };

  const findAirportByCode = (code: string) => {
    if (!searchData) return;
    const city = Object.values(searchData).find(city => city.airports.find(airport => airport.airportCode === code));
    const airport = city?.airports.find(airport => airport.airportCode === code);
    return airport;
  };

  if (isError) {
    throw queryError;
  }

  const isReady = isSuccess && data;
  return isReady
    ? ({ isSuccess: true, searchLocations, getLocationByIdentifier, findAirportByCode } as const)
    : ({ isSuccess: false } as const);
}

function search(searchTerm: string, cityMap: CityMap | null): City[] {
  if (!cityMap || !searchTerm || searchTerm.length < 2) return [];

  const q = searchTerm.toLowerCase();

  const parts = q
    .split(' ')
    .map(trim)
    .filter(q => q.length > 1);

  const airportResultScore: { city: City; score: number }[] = [];

  const cityArr = Object.values(cityMap);

  for (const city of cityArr) {
    let score = getScore(q, city);

    for (const part of parts) {
      score += getScore(part, city);
    }

    if (score > 0) {
      airportResultScore.push({ city, score });
    }
  }

  if (airportResultScore.length === 0) {
    return [];
  }

  airportResultScore.sort((a, b) => {
    if (a.score === b.score) {
      return a.city.cityName.localeCompare(b.city.cityName);
    }

    return b.score - a.score;
  });

  const airportResult = airportResultScore.map(({ city }) => city).slice(0, 5);

  return airportResult;
}

function getScore(q: string, city: City) {
  if (includesIgnoreCase(city.airportCodes, q)) return 19;

  if (equalsIgnoreCase(city.cityName, q)) return 9;
  if (city.airports.some(airport => equalsIgnoreCase(airport.airportName, q))) return 8;
  if (equalsIgnoreCase(city.countryName, q)) return 7;

  if (startsWithIgnoreCase(city.cityName, q)) return 6;
  if (city.airports.some(airport => startsWithIgnoreCase(airport.airportName, q))) return 5;
  if (startsWithIgnoreCase(city.countryName, q)) return 4;

  if (includesIgnoreCase(city.cityName, q)) return 3;
  if (city.airports.some(airport => includesIgnoreCase(airport.airportName, q))) return 2;
  if (includesIgnoreCase(city.countryName, q)) return 1;
  if (includesIgnoreCase(city.cityCode, q)) return 1;

  return 0;
}

function equalsIgnoreCase(a: string, b: string) {
  return a.toLowerCase() === b.toLowerCase();
}

function includesIgnoreCase(a: string, b: string) {
  return a.toLowerCase().includes(b.toLowerCase());
}

function startsWithIgnoreCase(a: string, b: string) {
  return a.toLowerCase().startsWith(b.toLowerCase());
}

function getByIdentifier(identifier: string, cityMap: CityMap | null) {
  if (!cityMap || !identifier || identifier.length !== 3) return null;
  const airportMatch = Object.values(cityMap).find(city => includesIgnoreCase(city.airportCodes, identifier));

  return airportMatch || cityMap[identifier] || null;
}
